import { donationFields, type DonationField } from "@baze/schema";
import type { SectionProps } from "../context";
import { domId, field, messageVars } from "../context";
import { FIELD, FormField, FormStatus, Honeypot, LABEL } from "../forms";
import { fillMessage, formatRupiah, waLink } from "../helpers";
import { Icon } from "../icons";
import { BUTTON, Container, MultiLine, REVEAL, SectionHeader, SectionShell, cx } from "../ui";

const DONATION_INTRO = "Halo {{nama_usaha}}, saya ingin berdonasi.";
const FORM_INTRO = "Halo {{nama_usaha}}, berikut data saya.";

/**
 * WhatsApp form (donations, registrations, orders…): the owner's questions (default name, WhatsApp,
 * address), then optionally an amount and a choice of account. Nothing is stored: live, the donation runtime
 * turns the answers into a WhatsApp message to the site's number (data-wa + data-intro), one "label: value"
 * line per answered question ([data-baze-field]). A card link adds ?untuk=<item>, which the runtime shows in
 * the read-only field labelled data-program-label.
 */
export function Donation({ node, ctx }: SectionProps<"donation">) {
  const c = node.content;
  const inert = ctx.editor || ctx.thumbnail;
  const wa = waLink(ctx.site.contact.whatsapp);
  const fields = donationFields(c);
  const askAmount = c.askAmount !== false;
  const askBank = c.askBank !== false && c.accounts.length > 0;
  const programLabel = c.programLabel || "Program";
  const split = node.variant === "split";
  const id = (s: string) => domId(ctx, `${node.id}-${s}`);
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container size={split ? "default" : "narrow"}>
        <div className={cx(split ? "grid gap-10 @3xl:grid-cols-2 @3xl:gap-14" : "")}>
          <div className={cx(split ? "@3xl:pt-2" : "text-center")}>
            <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} align={split ? "left" : "center"} />
            <MultiLine ctx={ctx} text={c.description} path="content.description" className={cx("mt-4 leading-relaxed text-site-muted", !split && "mx-auto max-w-xl")} />
          </div>
          <div className={cx(REVEAL, split ? "" : "mt-10")} data-baze-donation=""
            data-wa={wa ?? undefined}
            data-intro={fillMessage(c.message || (askAmount ? DONATION_INTRO : FORM_INTRO), messageVars(ctx))}
            data-program-label={programLabel}
          >
            <form className="relative space-y-5 rounded-site-lg border border-site-border bg-site-surface p-5 shadow-site-sm @md:p-7" noValidate>
              <div data-baze-program="" hidden>
                <FormField ctx={ctx} id={`${node.id}-program`} label={programLabel}>
                  <input id={id("program")} name="program" readOnly maxLength={80} className={cx(FIELD, "bg-site-surface")} />
                </FormField>
              </div>
              {fields.map((q, i) => (
                <FormField key={i} ctx={ctx} id={`${node.id}-f${i}`} label={q.required ? q.label : `${q.label} (opsional)`}>
                  <Question q={q} id={id(`f${i}`)} name={`f${i}`} disabled={inert} />
                </FormField>
              ))}
              {askAmount ? (
                <div>
                  <label htmlFor={id("amount")} className={LABEL}>
                    Nominal
                  </label>
                  {c.presets?.length ? (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {c.presets.map((v, i) => (
                        <button
                          key={i}
                          type="button"
                          data-baze-amount={v}
                          aria-pressed="false"
                          disabled={inert}
                          className="min-h-10 rounded-site-button border border-site-border bg-site-bg px-4 text-sm font-semibold text-site-text transition-colors aria-pressed:border-site-primary aria-pressed:bg-site-primary aria-pressed:text-site-on-primary motion-reduce:transition-none"
                          {...field(ctx, `content.presets.${i}`)}
                        >
                          {formatRupiah(v)}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <div className="relative">
                    <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-[0.9375rem] font-semibold text-site-muted">
                      Rp
                    </span>
                    <input id={id("amount")} name="amount" type="number" inputMode="numeric" required min={1000} step={1} disabled={inert} className={cx(FIELD, "pl-12")} placeholder="100000" />
                  </div>
                </div>
              ) : null}
              {askBank ? (
                <fieldset>
                  <legend className={LABEL}>Bank pilihan</legend>
                  <div className="grid gap-2 @md:grid-cols-2">
                    {c.accounts.map((acc, i) => {
                      const rid = id(`bank-${i}`);
                      return (
                        <div key={i}>
                          <input type="radio" id={rid} name="bank" value={i} required disabled={inert} data-label={`${acc.provider} – ${acc.number} a.n. ${acc.name}`} className="peer sr-only" />
                          <label
                            htmlFor={rid}
                            className={cx(
                              "flex h-full cursor-pointer flex-col gap-0.5 rounded-site-md border border-site-border bg-site-bg px-4 py-3 text-site-text transition-colors motion-reduce:transition-none",
                              "peer-checked:border-site-primary peer-checked:ring-2 peer-checked:ring-site-primary/30 peer-focus-visible:ring-2 peer-focus-visible:ring-site-primary/30",
                            )}
                          >
                            <span className="font-semibold" {...field(ctx, `content.accounts.${i}.provider`)}>
                              {acc.provider}
                            </span>
                            <span className="font-mono text-sm tracking-wide tabular-nums" {...field(ctx, `content.accounts.${i}.number`)}>
                              {acc.number}
                            </span>
                            <span className="text-xs text-site-muted">
                              a.n. <span {...field(ctx, `content.accounts.${i}.name`)}>{acc.name}</span>
                            </span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </fieldset>
              ) : null}
              <Honeypot />
              <div className="flex flex-wrap items-center gap-3">
                <button type="submit" disabled={inert || !wa} className={cx("baze-btn", BUTTON.primary, "disabled:cursor-not-allowed disabled:opacity-60")}>
                  <Icon name="whatsapp" className="size-[1.125rem]" />
                  <span {...field(ctx, "content.submitLabel")}>{c.submitLabel || "Kirim via WhatsApp"}</span>
                </button>
                <FormStatus />
              </div>
              {ctx.editor && !wa ? <p className="text-sm text-site-muted">Isi nomor WhatsApp di pengaturan situs agar formulir bisa dikirim.</p> : null}
            </form>
          </div>
        </div>
      </Container>
    </SectionShell>
  );
}

/** One owner-defined question. A "select" without choices falls back to a text box. */
function Question({ q, id, name, disabled }: { q: DonationField; id: string; name: string; disabled: boolean }) {
  const common = { id, name, required: q.required, disabled, "data-baze-field": "", "data-label": q.label };
  if (q.type === "textarea") return <textarea {...common} maxLength={500} rows={2} className={cx(FIELD, "resize-y")} placeholder={q.placeholder} />;
  if (q.type === "select" && q.options?.length) {
    return (
      <select {...common} defaultValue="" className={cx(FIELD, "appearance-none")}>
        <option value="" disabled={q.required}>
          {q.placeholder ?? "Pilih…"}
        </option>
        {q.options.map((o, i) => (
          <option key={i} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  if (q.type === "phone") return <input {...common} type="tel" inputMode="tel" pattern="[0-9+ \-]{8,20}" maxLength={20} autoComplete="tel" className={FIELD} placeholder={q.placeholder} />;
  if (q.type === "number") return <input {...common} type="number" inputMode="numeric" className={FIELD} placeholder={q.placeholder} />;
  return <input {...common} maxLength={120} className={FIELD} placeholder={q.placeholder} />;
}
